import { JSX, useState, useEffect, Dispatch, type MouseEvent, SetStateAction, useCallback, useMemo } from "react";

import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { IconButton, Tooltip } from "@mui/material";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { updateUserPinnedLocations } from "src/lib/settings.helper";
import useAppPinnedStore from "src/store/pinned.store";
import LocationType from "src/types/LocationType";

const PINNABLE_LOCATION_TYPES = [LocationType.Shop, LocationType.Mall, LocationType.Station];

function TogglePinned({
  locationId,
  type,
  setError = null,
  edge = "start",
  fontSize,
}: {
  locationId: string | undefined;
  type: LocationType | undefined;
  setError?: Dispatch<SetStateAction<string | null>> | ((msg: string | null) => void) | null;
  edge?: false | "end" | "start" | undefined;
  fontSize?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { appPinned, setAppPinned } = useAppPinnedStore();
  const [isPinned, setIsPinned] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  useEffect(() => {
    if (locationId) {
      setIsPinned(appPinned.some((item) => item.id === locationId));
    }
  }, [locationId, appPinned]);

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLElement>): Promise<void> => {
      event.stopPropagation();
      if (!locationId || isUpdating) return;

      setIsUpdating(true);

      const isPinned = appPinned.some((item) => item.id === locationId);
      const updatedAppPinned = isPinned ? appPinned.filter((item) => item.id !== locationId) : [...appPinned, { id: locationId, type }];

      ReactGA.event(isPinned ? "unpin_location" : "pin_location", {
        category: "Dashboard",
        label: locationId,
      });

      try {
        const updatedFromBackend = await updateUserPinnedLocations(updatedAppPinned);
        setAppPinned(updatedFromBackend);
        setIsPinned(!isPinned);
      } catch (error) {
        if (setError) {
          const msg = (error as Error).message || "";
          if (msg.includes("The maximum limit for Pinned")) {
            setError("You can pin up to five dashboards. Unpin one to add another.");
          } else {
            setError(`Error while updating pins: ${msg}`);
          }
        }
      } finally {
        setIsUpdating(false);
      }
    },
    [locationId, type, appPinned, setAppPinned, isUpdating, setError]
  );

  const iconStyle = useMemo(() => ({ fontSize }), [fontSize]);

  if (type && PINNABLE_LOCATION_TYPES.includes(type)) {
    return (
      <Tooltip title={isPinned ? t("Unpin") : t("Pin")} arrow>
        <IconButton edge={edge} id='lock-button' color='secondary' onClick={handleClick} disabled={!locationId || isUpdating}>
          {isPinned ? <PushPinIcon sx={iconStyle} /> : <PushPinOutlinedIcon sx={iconStyle} />}
        </IconButton>
      </Tooltip>
    );
  }
  return null;
}

export default TogglePinned;
